import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { AlertCircle } from "lucide-react";

export default function Login() {
    const { login } = useAuth();
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    const nextUrl = searchParams.get("next") || "";
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    // Post-login redirect: honor `?next=` when it's a same-origin path
    const resolveRedirect = (user) => {
        if (nextUrl && nextUrl.startsWith("/") && !nextUrl.startsWith("//")) {
            return nextUrl;
        }
        return user.role === "admin"
            ? "/admin"
            : user.role === "candidate"
              ? "/candidat"
              : "/client";
    };

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const user = await login(email, password);
            toast.success("Connexion réussie");
            nav(resolveRedirect(user));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Identifiants incorrects");
        } finally {
            setLoading(false);
        }
    };

    // Friendly banner when the user was redirected from a protected page
    const isFromDevis = nextUrl.startsWith("/devis");
    const bannerMsg = isFromDevis
        ? "Connectez-vous ou créez votre compte en 30 s pour finaliser votre devis. Vos réponses sont conservées."
        : nextUrl
          ? "Connectez-vous pour continuer."
          : "";

    return (
        <div>
            <Header />
            <div className="max-w-md mx-auto px-6 py-16">
                <div className="sd-card p-8 fade-up">
                    <div className="overline mb-2">Connexion</div>
                    <h1 className="font-display text-3xl mb-6">
                        Ravis de vous revoir.
                    </h1>
                    {bannerMsg && (
                        <div
                            className="mb-5 flex items-start gap-2 rounded-xl border border-[#F5A623]/40 bg-[#F5A623]/10 px-4 py-3 text-sm text-[#0B192C]"
                            data-testid="login-next-banner"
                        >
                            <AlertCircle className="shrink-0 mt-0.5 text-[#F5A623]" size={16} />
                            <span>{bannerMsg}</span>
                        </div>
                    )}
                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-semibold mb-1">
                                E-mail
                            </label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                data-testid="login-email-input"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold mb-1">
                                Mot de passe
                            </label>
                            <input
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus-ring bg-white"
                                data-testid="login-password-input"
                            />
                        </div>
                        <button
                            disabled={loading}
                            className="btn-gold w-full"
                            data-testid="login-submit-btn"
                        >
                            {loading ? "Connexion..." : "Me connecter"}
                        </button>
                    </form>
                    <div className="text-sm mt-4 text-right">
                        <Link
                            to="/mot-de-passe-oublie"
                            className="text-slate-500 hover:text-[#F5A623]"
                            data-testid="login-forgot-link"
                        >
                            Mot de passe oublié ?
                        </Link>
                    </div>
                    <div className="text-sm text-slate-500 mt-6 space-y-2">
                        <div>
                            Pas encore de compte client ?{" "}
                            <Link
                                to={nextUrl ? `/inscription?next=${encodeURIComponent(nextUrl)}` : "/inscription"}
                                className="text-[#0B192C] font-semibold hover:text-[#F5A623]"
                                data-testid="login-register-client-link"
                            >
                                Créer un compte entreprise
                            </Link>
                        </div>
                        <div>
                            Vous êtes un professionnel ?{" "}
                            <Link
                                to="/candidat/inscription"
                                className="text-[#0B192C] font-semibold hover:text-[#F5A623]"
                                data-testid="login-register-candidate-link"
                            >
                                Créer un compte candidat
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
}
